import { ProFormField } from '@ant-design/pro-form';
import {
  Menu,
  Select,
  Tabs,
  Tooltip,
} from 'antd';
import { observer } from 'mobx-react-lite';
import React, {
  FC,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { VersionSelectFormType } from '@/presentation/components/form';
import {
  getValueFromEvent,
  useOptions,
} from '@/presentation/components/form/utils';
import {
  ExOption,
  FIELD_ALL,
  FieldKey,
  Option,
  VersionFilter,
  Zone,
} from '@/proto/field';
import style from './version-select.module.scss';
export interface VersionType {
  operator: string;
  values: string[];
  name: string;
}

const VersionSelect: FC<{
  entry: VersionSelectFormType,
  value?: VersionType,
  onChange?: (value: VersionType) => void,
}> = observer((props) => {
  const { t } = useTranslation();
  const { entry } = props;
  const [value, setValue] = useState<VersionType>({
    operator: VersionFilter.Or,
    values: [FIELD_ALL],
    name: entry.versionName,
  });
  const [isFocus, setIsFocus] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const supportNotMatch = entry.fieldKey === FieldKey.AppVersion && typeof entry.fieldScope === 'string'
    && Object.values(Zone).includes(entry.fieldScope as Zone);

  const options = useOptions({
    fieldKey: entry.fieldKey,
    fieldScope: entry.fieldScope,
  });

  useEffect(() => {
    if (props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  const onTabClick = (key: string) => {
    let operator;
    if (key === 'adv') {
      operator = VersionFilter.Ge;
    } else {
      operator = VersionFilter.Or;
    }
    const newValue = {
      ...value,
      values: [],
      operator,
    };
    setValue(newValue);
    props.onChange?.(newValue);
  };

  const onOperatorClick = (operator: VersionFilter) => {
    const newValue = {
      ...value,
      values: [],
      operator,
    };
    setValue(newValue);
    props.onChange?.(newValue);
  };

  const getMode = () => {
    if (value.operator === VersionFilter.Or || value.operator === VersionFilter.Not) {
      return 'multiple';
    }
    return undefined;
  };

  const getTab = () => {
    if (value.operator === VersionFilter.Or) {
      return 'multiple';
    }
    return 'adv';
  };

  const onChange = (version: string | string[]) => {
    const formatVersion = getValueFromEvent(
      typeof version === 'string' ? [version] : version,
      getMode() || 'single',
    );
    const newValue = {
      ...value,
      values: formatVersion,
    };
    setValue(newValue);
    props.onChange?.(newValue);
  };

  const filterOption = (input: string, option: Option | ExOption | undefined) => {
    if (option && typeof option.label === 'string') {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    return false;
  };

  const wording = {
    [VersionFilter.Or]: t('包含'),
    [VersionFilter.Not]: t('不包含'),
    [VersionFilter.Ge]: t('大于等于'),
    [VersionFilter.Le]: t('小于等于'),
  };

  const dropdownRender = (menu: React.ReactElement) => (
    <div className={style.dropdown}>
      <Tabs activeKey={getTab()} onTabClick={onTabClick}>
        <Tabs.TabPane tab={t('多选模式')} key={'multiple'}>
          <div data-tid={`${entry.name}-multiple`} >
            {menu}
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('高级模式')} key={'adv'}>
          <div className={style.adv} data-tid={`${entry.name}-adv`}>
            <Menu selectedKeys={[value.operator]} onClick={e => onOperatorClick(e.key as VersionFilter)}>
              <Menu.Item className={style.menuItem} key={VersionFilter.Ge}>{wording[VersionFilter.Ge]}</Menu.Item>
              <Menu.Item className={style.menuItem} key={VersionFilter.Le}>{wording[VersionFilter.Le]}</Menu.Item>
              {supportNotMatch
                ? <Menu.Item className={style.menuItem} key={VersionFilter.Not}>{wording[VersionFilter.Not]}</Menu.Item>
                : null
              }
            </Menu>
            <div className={style.version}>
              {menu}
            </div>
          </div>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );

  const renderFakeWording = () => {
    const show = !isFocus && value.operator !== VersionFilter.Or;
    if (!show) {
      return null;
    }

    const operatorLabel = wording[value.operator as VersionFilter];
    const valueLabel = !value.values || value.values.includes(FIELD_ALL) ? t('所有') : value.values;
    return <span className={style.fakeWording}>
      {`${operatorLabel}${valueLabel}`}
    </span>;
  };

  const tips = <>{value.values.map(item => <div>{item}</div>)}</>;

  const tipsVisible = isHover && !isFocus && !dropdownVisible && value.values.length > 1;

  const renderOptions = getTab() === 'adv' ? options.filter(item => item.value !== FIELD_ALL) : options;

  return <Tooltip title={tips} visible={tipsVisible}>
    <div className={style.versionSelect}>
      <Select
        showSearch
        options={renderOptions?.map(item => ({
          ...item,
          ['data-tid']: `${entry.name}-${item.label}`,
        }))}
        mode={getMode()}
        placeholder={t('所有')}
        maxTagCount={'responsive'}
        dropdownMatchSelectWidth={300}
        dropdownRender={dropdownRender}
        onDropdownVisibleChange={open => setDropdownVisible(open)}
        value={value.values}
        onChange={onChange}
        filterOption={filterOption}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        dropdownClassName={'reset-font-size'}
      />
      {renderFakeWording()}
    </div>
  </Tooltip>;
});

export const VersionSelectForm: FC<{
  entry: VersionSelectFormType,
  hidden?: boolean,
}> = (props) => {
  const { entry, hidden } = props;
  const { name, label } = entry;

  return <ProFormField name={name} label={label} hidden={hidden}>
    <VersionSelect entry={entry} />
  </ProFormField>;
};
